import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Card,
  CardContent,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { getExpoChannels, getExpoReleases } from '../../services/expoUpdates';
import type { ExpoChannel, ExpoRelease } from '../../models/expo';

const ExpoUpdates: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [channels, setChannels] = useState<ExpoChannel[]>([]);
  const [releases, setReleases] = useState<ExpoRelease[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Load data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [channelsData, releasesData] = await Promise.all([
          getExpoChannels(),
          getExpoReleases(),
        ]);
        setChannels(channelsData);
        setReleases(releasesData);
      } catch (err) {
        console.error('Failed to load Expo update data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, []);

  // Channel table columns
  const channelColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'app_name', headerName: 'App', width: 150 },
    { field: 'name', headerName: 'Name', flex: 1 },
    { field: 'description', headerName: 'Description', flex: 2 },
    { field: 'created_at', headerName: 'Created', width: 200 },
  ];

  // Release table columns
  const releaseColumns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'app_name', headerName: 'App', width: 120 },
    { field: 'channel_name', headerName: 'Channel', width: 120 },
    { field: 'version', headerName: 'Version', width: 120 },
    { field: 'runtime_version', headerName: 'Runtime Version', width: 150 },
    { 
      field: 'platform', 
      headerName: 'Platform', 
      width: 100,
      renderCell: (params) => {
        return params.value === 'all' ? 'All Platforms' : params.value;
      }
    },
    { 
      field: 'status', 
      headerName: 'Status', 
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        let color = '';
        switch(status) {
          case 'published':
            color = '#4caf50'; // Green
            break;
          case 'draft':
            color = '#ffc107'; // Amber
            break;
          case 'rolled-back':
            color = '#f44336'; // Red
            break;
        }
        
        return (
          <Box 
            sx={{ 
              backgroundColor: color,
              color: 'white',
              borderRadius: '4px',
              padding: '3px 10px',
              textTransform: 'capitalize'
            }}
          >
            {status}
          </Box>
        );
      }
    },
    { 
      field: 'rollout_percentage', 
      headerName: 'Rollout %', 
      width: 100,
      renderCell: (params) => {
        return `${params.value}%`;
      }
    },
    { 
      field: 'is_mandatory', 
      headerName: 'Mandatory', 
      width: 100,
      renderCell: (params) => {
        return params.value ? 'Yes' : 'No';
      }
    },
    { field: 'created_at', headerName: 'Created', width: 200 },
  ];

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [channelsData, releasesData] = await Promise.all([
        getExpoChannels(),
        getExpoReleases(),
      ]);
      setChannels(channelsData);
      setReleases(releasesData);
    } catch (err) {
      console.error('Failed to refresh Expo update data:', err);
      setError('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPublishedReleaseCount = () => {
    return releases.filter(release => release.status === 'published').length;
  };

  const getDraftReleaseCount = () => {
    return releases.filter(release => release.status === 'draft').length;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Expo Updates
        </Typography>
        <Box>
          <Button 
            variant="outlined"
            startIcon={<RefreshIcon />} 
            onClick={refreshData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/expo-updates/channels/create')}
            sx={{ mr: 2 }}
          >
            New Channel
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => navigate('/expo-updates/releases/create')}
          >
            New Release
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {channels.length}
              </Typography>
              <Typography color="text.secondary">
                Update Channels
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {releases.length}
              </Typography>
              <Typography color="text.secondary">
                Total Releases
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {getPublishedReleaseCount()}
              </Typography>
              <Typography color="text.secondary">
                Published Releases
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h5" component="div" gutterBottom>
                {getDraftReleaseCount()}
              </Typography>
              <Typography color="text.secondary">
                Draft Releases
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Channels" />
          <Tab label="Releases" />
        </Tabs>
        <Divider />
        
        <Box sx={{ p: 2, height: 500 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {tabValue === 0 && (
                <DataGrid
                  rows={channels}
                  columns={channelColumns}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                    sorting: {
                      sortModel: [{ field: 'name', sort: 'asc' }],
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  onRowClick={(params) => navigate(`/expo-updates/channels/${params.row.id}`)}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              {tabValue === 1 && (
                <DataGrid
                  rows={releases}
                  columns={releaseColumns}
                  initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                    sorting: {
                      sortModel: [{ field: 'created_at', sort: 'desc' }],
                    },
                  }}
                  pageSizeOptions={[10, 25, 50]}
                  onRowClick={(params) => navigate(`/expo-updates/releases/${params.row.id}`)}
                  sx={{ cursor: 'pointer' }}
                />
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ExpoUpdates;
